import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { http, errorText } from "../../lib/api";

const TYPES = ["Request a quote", "Technical enquiry", "Distribution partnership"];
const PRODUCTS = ["PALATIVA", "OPTIBITE", "AVITIVA", "Not sure yet"];

const empty = { name: "", company: "", email: "", phone: "", country: "India", species: "", message: "" };

const Chip = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={`rounded-full border px-4 py-2 text-sm font-medium transition-[background-color,color,border-color] duration-300 ${
      active ? "bg-[var(--forest)] text-[var(--bone)] border-[var(--forest)]" : "border-[var(--line)] text-[var(--ink)] hover:border-[var(--forest)]"
    }`}
  >
    {children}
  </button>
);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="eyebrow text-[11px] text-[var(--muted)]">{label}</span>
    {children}
  </label>
);

export const EnquiryForm = ({ defaultProduct = "PALATIVA" }) => {
  const [type, setType] = useState(TYPES[0]);
  const [product, setProduct] = useState(defaultProduct);
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.post("/enquiries", { ...f, enquiry_type: type, product });
      setDone(true);
      setF(empty);
      toast.success("Thank you — our team will reply within one business day.");
    } catch (err) {
      toast.error(errorText(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <motion.div data-testid="enquiry-success" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="py-16">
        <span className="grid place-items-center h-14 w-14 rounded-full bg-[var(--forest)] text-[#8DC63F]"><Check /></span>
        <h3 className="font-display text-3xl mt-6">Enquiry received.</h3>
        <p className="text-[var(--muted)] mt-3 max-w-md">Our technical team will get back to you within one business day with pricing, samples or dosage guidance.</p>
        <button data-testid="enquiry-new-button" onClick={() => setDone(false)} className="mt-8 link-underline font-semibold">Send another enquiry</button>
      </motion.div>
    );
  }

  return (
    <form data-testid="enquiry-form" onSubmit={submit} className="space-y-8">
      <div>
        <p className="eyebrow text-[11px] text-[var(--muted)] mb-3">I would like to</p>
        <div className="flex flex-wrap gap-2">
          {TYPES.map((t) => <Chip key={t} testId={`enquiry-type-${t.split(" ")[0].toLowerCase()}`} active={type === t} onClick={() => setType(t)}>{t}</Chip>)}
        </div>
      </div>
      <div>
        <p className="eyebrow text-[11px] text-[var(--muted)] mb-3">Product range</p>
        <div className="flex flex-wrap gap-2">
          {PRODUCTS.map((p) => <Chip key={p} testId={`enquiry-product-${p.split(" ")[0].toLowerCase()}`} active={product === p} onClick={() => setProduct(p)}>{p}</Chip>)}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
        <Field label="Full name *"><input data-testid="enquiry-name-input" required minLength={2} className="field" value={f.name} onChange={set("name")} placeholder="Your name" /></Field>
        <Field label="Company / feed mill *"><input data-testid="enquiry-company-input" required className="field" value={f.company} onChange={set("company")} placeholder="Company name" /></Field>
        <Field label="Work email *"><input data-testid="enquiry-email-input" required type="email" className="field" value={f.email} onChange={set("email")} placeholder="name@company.com" /></Field>
        <Field label="Phone"><input data-testid="enquiry-phone-input" className="field" value={f.phone} onChange={set("phone")} placeholder="+91" /></Field>
        <Field label="Country"><input data-testid="enquiry-country-input" className="field" value={f.country} onChange={set("country")} /></Field>
        <Field label="Species / feed type"><input data-testid="enquiry-species-input" className="field" value={f.species} onChange={set("species")} placeholder="e.g. calf starter, vannamei, layer" /></Field>
      </div>
      <Field label="Message">
        <textarea data-testid="enquiry-message-input" rows={3} className="field resize-none" value={f.message} onChange={set("message")} placeholder="Volumes, target dosage, sample requirements…" />
      </Field>
      <button
        type="submit"
        disabled={busy}
        data-testid="enquiry-submit-button"
        className="group inline-flex items-center gap-3 rounded-full bg-[var(--forest)] text-[var(--bone)] pl-7 pr-2 py-2 font-semibold transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
      >
        {busy ? "Sending…" : "Send enquiry"}
        <span className="grid place-items-center h-10 w-10 rounded-full bg-[#8DC63F] text-[var(--forest)] transition-transform duration-300 group-hover:translate-x-1">
          {busy ? <Loader2 size={18} className="animate-spin" /> : <ArrowRight size={18} />}
        </span>
      </button>
    </form>
  );
};
